import { Button, Card, Typography } from '@sl/react-datafabric-components';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSubscribeMutation } from '../../../../common/redux/services/subscriptions';
import { useGetTransactionQuery } from '../../../../common/redux/services/transactions';

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionID = searchParams.get('session_id');
  const isDowngrade = searchParams.get('downgrade') === 'true';
  const [status, setStatus] = useState<
    | 'verifiying-payment'
    | 'lost'
    | 'confirm-payment'
    | 'confirm-downgrade'
    | undefined
  >();

  const { data: item, error } = useGetTransactionQuery(sessionID, {
    skip: !sessionID,
  });

  const [createSubscription] = useSubscribeMutation();

  useEffect(() => {
    if (isDowngrade) {
      setStatus('confirm-downgrade');
      return;
    }

    if (!sessionID) {
      setStatus('lost');
      return;
    }

    // Step 1: Check if the stored session is different from the current one
    const storedSessionId = sessionStorage.getItem('lastSessionId');

    if (storedSessionId !== sessionID) {
      sessionStorage.removeItem('subscriptionConfirmed'); // Reset only when session changes
      sessionStorage.setItem('lastSessionId', sessionID); // Store new session ID
    }

    // Step 2: If already confirmed, do nothing
    if (sessionStorage.getItem('subscriptionConfirmed')) {
      setStatus('confirm-payment');
      return;
    }

    async function confirmPayment() {
      setStatus('verifiying-payment');

      if (!item?.data?.metadata) {
        return;
      }

      await createSubscription(item.data.metadata).unwrap();

      sessionStorage.setItem('subscriptionConfirmed', 'true');

      window.location.reload();
    }

    // Step 3: Run confirmPayment() only if not already confirmed
    if (!sessionStorage.getItem('subscriptionConfirmed')) {
      confirmPayment();
    }
  }, [sessionID, item, createSubscription, error, isDowngrade]);

  const handleGoHome = () => {
    navigate('/', { replace: true });
  };

  const handleTryAgain = () => {
    navigate('/subscriptions/subscribe', { replace: true });
  };

  return (
    <Card className='w-full max-w-md'>
      <div className='text-center p-6'>
        {status === 'lost' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-exclamation-triangle text-6xl text-orange-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Uh-oh! It seems like you're lost.
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              We couldn't find a valid subscription session. Please try again.
            </Typography>

            {/* Action Buttons */}
            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Try Again'
                icon='pi pi-refresh'
                onClick={handleTryAgain}
                severity='info'
              />
              <Button
                label='Go Home'
                icon='pi pi-home'
                onClick={handleGoHome}
                outlined
              />
            </div>
          </>
        ) : status === 'confirm-payment' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-check-circle text-6xl text-green-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Payment Successful!
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              Your subscription has been activated successfully. Welcome aboard!
            </Typography>

            {/* Action Buttons */}
            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Go to Dashboard'
                icon='pi pi-home'
                onClick={handleGoHome}
                severity='success'
              />
            </div>
          </>
        ) : status === 'confirm-downgrade' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-check-circle text-6xl text-green-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Subscription Updated!
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              You will remain on your current plan until the end of the billing
              cycle, then switch to your new plan.
            </Typography>

            {/* Action Buttons */}
            <div className='flex flex-column gap-3 align-items-center'>
              <Button
                label='Go to Dashboard'
                icon='pi pi-home'
                onClick={handleGoHome}
                severity='success'
              />
            </div>
          </>
        ) : status === 'verifiying-payment' ? (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-spin pi-spinner text-6xl text-blue-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Verifying Payment
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              Please wait while we verify your payment...
            </Typography>
          </>
        ) : (
          <>
            {/* Icon */}
            <div className='mb-4'>
              <i className='pi pi-spin pi-spinner text-6xl text-blue-500'></i>
            </div>

            {/* Title */}
            <Typography className='text-2xl font-bold text-900'>
              Loading
            </Typography>

            {/* Description */}
            <Typography className='text-600 line-height-3 mb-8'>
              Please wait while we process your request...
            </Typography>
          </>
        )}
      </div>
    </Card>
  );
}
